import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import MarketplaceFeedDetailsModal from './MarketPlaceFeedDetailsModal';
import useMarketPlaceVerificationStatus from '../../hooks/useMarketPlaceVerificationStatus';
import FileViewer from './FileViewer';
import MarketPlaceOpportunityDetailsModal from './MarketPlaceOpportunityDetailsModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MarketPlaceFeedOpportunities = () => {
  const navigate = useNavigate();
  const { isVerified } = useMarketPlaceVerificationStatus();
  const [feedOpportunities, setFeedOpportunities] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [type, setType] = useState("");
  const [searchType, setSearchType] = useState("");
  const [postId, setPostId] = useState(null);
  const [open, setOpen] = useState(false);

  const pageRef = useRef(1);
  const observer = useRef(null);

  // ---------------- FETCH ----------------
  const fetchOpportunities = async (pageNum, isNew = false) => {
    if (loading) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${BASE_URL}/marketplace/researcher/opportunity/?page=${pageNum}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'x-api-key': process.env.REACT_APP_X_API_KEY,
          },
        }
      );

      const newData = response.data.results || response.data;

      setFeedOpportunities((prev) =>
        isNew || pageNum === 1 ? newData : [...prev, ...newData]
      );

      setHasMore(!!response.data.next);
      pageRef.current = pageNum + 1;

    } catch (error) {
      console.error('Error fetching opportunities:', error);

      if (pageNum === 1) {
        setFeedOpportunities([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchOpportunities(1, true);
  }, []);

  // ---------------- INFINITE SCROLL ----------------
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loading) {
            fetchOpportunities(pageRef.current, false);
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // cleanup
  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  const updateArticleReads = (articleId, newReads) => {
    setFeedOpportunities(prev => prev.map(a => a.id === articleId ? {...a, views: newReads} : a));
  };

  // ---------------- UI ----------------
  return (
    <>
      <div className="flex flex-col gap-4 flex-1 w-full">

        <section className="w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">

          <div className="flex items-center gap-3 p-4 bg-[#e8f0f7] rounded-xl mb-6">
            <div className="text-sm font-semibold text-gray-900">
              Researcher Opportunities
            </div>
          </div>

          {feedOpportunities?.map((p, i) => {
            const isLast = i === feedOpportunities?.length - 1;

            return (
              <div
                key={p.id}
                ref={isLast ? lastItemRef : null}
                className={`bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 ${isVerified ? 'cursor-pointer' : ''} hover:border-[#0f6abf] `}
                onClick={() => {
                  if(isVerified)
                  {
                    window.history.pushState(
                    {},
                    "",
                    `/marketplace/feed/opportunity/${p.id}?research_type=${p.research_type}&type=${p.publication_type}`
                  );

                  setType(p?.publication_type);
                  setSearchType(p?.research_type);
                  setPostId(p?.id);
                  setOpen(true);
                  }
                }}
              >

                {/* HEADER */}
                <div className="flex items-center justify-between mb-3">

                  <div className="bg-[#e8f0f7] text-[#0f6abf] text-xs font-semibold px-3 py-1 rounded-lg">
                    {p.university_data ? p?.university_data?.university_name : p?.profile_data?.name}
                  </div>

                  <div className="flex items-center gap-1 text-gray-600 text-sm">
                    <Eye className="w-4 h-4" />
                    <span>{p?.views}</span>
                  </div>

                  <img
                    src={
                      p?.university_data
                        ? p?.university_data.university_logo
                        : p?.profile_data?.profile_picture
                    }
                    className="w-8 h-8 rounded-full object-cover border"
                    onClick={(e) => {
                     if(isVerified)
                     {
                       e.stopPropagation();
                      navigate(
                        p?.university_data
                          ? `/marketplace/university/${p?.university_data?.id}`
                          : `/marketplace/independent_researcher/${p?.researcher}`
                      );
                     }
                    }}
                  />
                </div>

                {/* TAGS */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs px-3 py-1 bg-gray-100 rounded-lg">{p?.domain}</span>
                  <span className="text-xs px-3 py-1 bg-blue-100 rounded-lg">{p?.collaboration}</span>
                  <span className="text-xs px-3 py-1 bg-gray-100 rounded-lg">{p?.maturity}</span>
                  <span className="text-xs px-3 py-1 bg-green-100 rounded-lg">{p?.visibility}</span>
                </div>
 <div className="text-lg font-bold text-gray-900 mb-2 mt-2">
                {p.name}
              </div>
              {
                     !isVerified && <p>
                          {p.summary}
                     </p>
                  }
                  { 
                    !isVerified && <button
                  onClick={() => navigate('/marketplace')}
                  className="mt-6 w-full py-3 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center justify-center gap-2"
                >
                  Unlock or Join MarketPlace to View More
                </button>
                  }
                {/* MEDIA */}
                    {
                    isVerified && 
                    <>
                    <FileViewer publication_type={p?.publication_type} file={p?.file} name={p?.name} />
                    </>
                  }


              </div>
            );
          })}

          {/* LOADING STATE */}
      {loading && (
        <div className="flex justify-center py-6">
          <div className="text-gray-500 text-sm">Loading...</div>
        </div>
      )}

      {/* Load more indicator */}
      {!loading && hasMore && feedOpportunities?.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">Scroll to load more</div>
        </div>
      )}

      {/* End of list indicator */}
      {!loading && !hasMore && feedOpportunities?.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="text-gray-400 text-sm">No more opportunities</div>
        </div>
      )}


        </section>
      </div>
        <MarketPlaceOpportunityDetailsModal
        id={postId}
        type={type}
        research_type={searchType}
        isOpen={open}
        onClose={() => setOpen(false)}
        onReadsUpdate={updateArticleReads}
        />
      {/* <MarketplaceOpportunityModa
        id={postId}
        type={type}
        research_type={searchType}
        isOpen={open}
        onClose={() => setOpen(false)}
        onReadsUpdate={updateArticleReads}
      /> */}
    </>
  );
};

export default MarketPlaceFeedOpportunities;

